import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { EMOJI_DATA } from '../data/emojiData';

export interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose?: () => void;
}

export function EmojiPicker({ onSelect, onClose }: EmojiPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredEmojis = useMemo(() => {
    if (!searchTerm) return EMOJI_DATA;

    const lowerTerm = searchTerm.toLowerCase();
    return EMOJI_DATA.filter(
      (emoji) =>
        emoji.name.includes(lowerTerm) ||
        emoji.keywords.some((k) => k.includes(lowerTerm))
    );
  }, [searchTerm]);

  const displayedContent = useMemo(() => {
    if (searchTerm) {
      return (
        <div className="grid grid-cols-6 gap-2">
          {filteredEmojis.map((emoji, idx) => (
            <button
              key={`${emoji.char}-${idx}`}
              type="button"
              onClick={() => onSelect(emoji.char)}
              className="text-2xl p-2 hover:bg-slate-100 rounded transition-colors flex items-center justify-center"
              title={emoji.name}
            >
              {emoji.char}
            </button>
          ))}
          {filteredEmojis.length === 0 && (
            <div className="col-span-6 text-center text-slate-500 py-4 text-sm">
              Nessuna emoji trovata
            </div>
          )}
        </div>
      );
    }

    const categories = EMOJI_DATA.reduce(
      (acc, emoji) => {
        const bucket = (acc[emoji.category] ??= []);
        bucket.push(emoji);
        return acc;
      },
      {} as Record<string, typeof EMOJI_DATA>
    );

    return Object.entries(categories).map(([category, emojis]) => (
      <div key={category} className="mb-4">
        <h4 className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider sticky top-0 bg-white py-1">
          {category}
        </h4>
        <div className="grid grid-cols-6 gap-2">
          {emojis.map((emoji, idx) => (
            <button
              key={`${emoji.char}-${idx}`}
              type="button"
              onClick={() => onSelect(emoji.char)}
              className="text-2xl p-2 hover:bg-slate-100 rounded transition-colors flex items-center justify-center"
              title={emoji.name}
            >
              {emoji.char}
            </button>
          ))}
        </div>
      </div>
    ));
  }, [filteredEmojis, searchTerm, onSelect]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg w-full max-w-sm flex flex-col h-80">
      <div className="p-3 border-b border-slate-100 flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Cerca emoji..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--tl4k-primary-300)] bg-slate-50"
            autoFocus
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3">{displayedContent}</div>
    </div>
  );
}

export default EmojiPicker;
